import { Link } from "react-router-dom";
import {
  Shield, Activity, Puzzle, MessageSquareText, Clock, Zap, LayoutDashboard,
  CheckCircle2, Globe, Lock, ArrowRight, Bell, RefreshCw, Eye, ChevronRight,
} from "lucide-react";
import { Button } from "../components/ui/button";

const scrollTo = (id) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
};

const Nav = () => (
  <nav className="fixed top-0 left-0 right-0 z-50 border-b border-zinc-800/60 bg-zinc-950/80 backdrop-blur-xl">
    <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-amber-500/10 border border-amber-500/25 flex items-center justify-center">
          <Shield className="w-5 h-5 text-amber-500" />
        </div>
        <span className="text-slate-50 font-bold font-['Outfit'] text-lg tracking-tight">
          WordPress Sheriff
        </span>
      </div>
      <div className="hidden md:flex items-center gap-8">
        <button data-testid="nav-features-link" onClick={() => scrollTo("features")} className="text-sm text-zinc-400 hover:text-amber-400 transition-colors">Features</button>
        <button data-testid="nav-how-it-works-link" onClick={() => scrollTo("how-it-works")} className="text-sm text-zinc-400 hover:text-amber-400 transition-colors">How It Works</button>
        <Link data-testid="nav-dashboard-link" to="/dashboard" className="text-sm text-zinc-400 hover:text-amber-400 transition-colors">Dashboard</Link>
        <button data-testid="nav-pricing-link" onClick={() => scrollTo("pricing")} className="text-sm text-zinc-400 hover:text-amber-400 transition-colors">Pricing</button>
      </div>
      <div className="flex items-center gap-3">
        <Link to="/dashboard">
          <Button data-testid="nav-view-demo-btn" variant="outline" className="hidden sm:inline-flex h-9 border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-800 hover:text-slate-50 rounded-md text-sm">
            View Demo
          </Button>
        </Link>
        <Link to="/dashboard">
          <Button data-testid="nav-start-monitoring-btn" className="h-9 bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold rounded-md text-sm">
            Start Monitoring
          </Button>
        </Link>
      </div>
    </div>
  </nav>
);

const HeroPreviewCard = () => (
  <div data-testid="hero-preview-card" className="relative">
    <div className="absolute -inset-6 bg-amber-500/10 rounded-3xl blur-3xl pointer-events-none" />
    <div className="relative bg-zinc-900 backdrop-blur-md border border-zinc-700 rounded-2xl p-7 shadow-2xl shadow-black/50 lg:scale-[1.04] lg:origin-center">
      {/* Card header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-md bg-amber-500/15 border border-amber-500/35 flex items-center justify-center">
            <Shield className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <p className="text-white text-base font-semibold leading-none">Sheriff Dashboard</p>
            <p className="text-zinc-400 text-xs mt-1">Last check: just now</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-400/40">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-emerald-300 text-xs font-semibold">All Clear</span>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-zinc-800/80 border border-zinc-700 rounded-lg p-3.5">
          <p className="text-3xl font-bold text-white font-['Outfit']">4</p>
          <p className="text-zinc-300 text-xs mt-1">Sites Monitored</p>
        </div>
        <div className="bg-zinc-800/80 border border-zinc-700 rounded-lg p-3.5">
          <p className="text-3xl font-bold text-emerald-400 font-['Outfit']">4</p>
          <p className="text-zinc-300 text-xs mt-1">Online</p>
        </div>
        <div className="bg-zinc-800/80 border border-zinc-700 rounded-lg p-3.5">
          <p className="text-3xl font-bold text-white font-['Outfit']">0</p>
          <p className="text-zinc-300 text-xs mt-1">Active Alerts</p>
        </div>
      </div>

      {/* Uptime bar */}
      <div className="bg-zinc-800/80 border border-zinc-700 rounded-lg p-3.5 mb-4">
        <div className="flex items-center justify-between mb-2.5">
          <span className="text-zinc-200 text-sm font-medium">Uptime (30 days)</span>
          <span className="text-emerald-300 text-sm font-bold">100%</span>
        </div>
        <div className="flex gap-[3px]">
          {Array.from({ length: 24 }).map((_, i) => (
            <div key={i} className="h-7 flex-1 rounded-sm bg-emerald-400" />
          ))}
        </div>
      </div>

      {/* Updates row */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="flex items-center gap-3 bg-zinc-800/80 border border-zinc-700 rounded-lg p-3.5">
          <Puzzle className="w-5 h-5 text-amber-400 shrink-0" />
          <div>
            <p className="text-white text-sm font-semibold">Plugin Updates</p>
            <p className="text-zinc-300 text-xs">2 available</p>
          </div>
        </div>
        <div className="flex items-center gap-3 bg-zinc-800/80 border border-zinc-700 rounded-lg p-3.5">
          <RefreshCw className="w-5 h-5 text-amber-400 shrink-0" />
          <div>
            <p className="text-white text-sm font-semibold">Theme Updates</p>
            <p className="text-zinc-300 text-xs">4 available</p>
          </div>
        </div>
      </div>

      {/* Recently monitored */}
      <div className="flex items-center justify-between bg-zinc-800/80 border border-zinc-700 rounded-lg p-3.5">
        <div className="flex items-center gap-3">
          <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
          <div>
            <p className="text-white text-sm font-semibold">backyardbuyersguide.com</p>
            <p className="text-zinc-300 text-xs">Recently monitored · Online</p>
          </div>
        </div>
        <span className="text-emerald-300 text-xs font-semibold px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-400/35">Clean</span>
      </div>
    </div>
  </div>
);

const FEATURES = [
  {
    icon: Activity,
    title: "Uptime Watch",
    desc: "Know when your site goes offline before customers do.",
  },
  {
    icon: Puzzle,
    title: "Plugin & Theme Checks",
    desc: "Spot outdated WordPress items before they become problems.",
  },
  {
    icon: MessageSquareText,
    title: "Plain-English Alerts",
    desc: "No tech jargon. Just what happened, why it matters, and what to do next.",
  },
];

const STEPS = [
  {
    icon: Globe,
    step: "01",
    title: "Add your site",
    desc: "Paste your WordPress URL. Connect the lightweight Sheriff connector for deeper checks — done in under two minutes.",
  },
  {
    icon: Eye,
    step: "02",
    title: "Sheriff patrols 24/7",
    desc: "Automatic checks run around the clock — uptime, WordPress version, plugin and theme updates.",
  },
  {
    icon: Bell,
    step: "03",
    title: "Get warned early",
    desc: "If something looks wrong, you get a clear alert with what happened and what to do next.",
  },
];

const BENEFITS = [
  { icon: Clock, title: "24/7 Monitoring", desc: "Your site is watched around the clock, even while you sleep." },
  { icon: Shield, title: "WordPress Focused", desc: "Built specifically for WordPress — not a generic ping tool." },
  { icon: Zap, title: "Instant Alerts", desc: "Email alerts the moment something needs your attention." },
  { icon: LayoutDashboard, title: "Simple Dashboard", desc: "One clean view: clean, needs attention, or in trouble." },
];

const PRICING = [
  {
    name: "Free Demo",
    price: "$0",
    period: "",
    desc: "Explore the Sheriff dashboard with a live demo.",
    features: ["Live dashboard access", "1 monitored site", "Basic uptime checks"],
    cta: "View Demo",
    highlight: false,
  },
  {
    name: "Solo Site",
    price: "Coming Soon",
    period: "",
    desc: "Full protection for one WordPress site.",
    features: ["1 WordPress site", "24/7 monitoring", "Email alerts", "Plugin & theme checks"],
    cta: "Start Monitoring",
    highlight: true,
  },
  {
    name: "Business Watch",
    price: "Coming Soon",
    period: "",
    desc: "For owners managing multiple sites.",
    features: ["Up to 10 sites", "Priority checks", "Autonomous recovery", "Email alerts"],
    cta: "Start Monitoring",
    highlight: false,
  },
];

export default function LandingPage() {
  return (
    <div data-testid="landing-page" className="min-h-screen bg-zinc-950 text-slate-50 overflow-x-hidden">
      <Nav />

      {/* ===== HERO ===== */}
      <section className="relative pt-32 pb-20 px-6">
        {/* Ambient glows */}
        <div className="absolute top-0 left-1/4 w-[500px] h-[400px] bg-amber-500/6 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute top-40 right-0 w-[400px] h-[400px] bg-emerald-500/5 rounded-full blur-[120px] pointer-events-none" />

        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-center relative">
          <div>
            <div data-testid="hero-badge" className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/25 mb-7">
              <Zap className="w-3.5 h-3.5 text-amber-400" />
              <span className="text-amber-400 text-xs font-medium tracking-wide">Built with Emergent for the Build Challenge</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-['Outfit'] tracking-tight leading-[1.08] mb-6">
              Your WordPress site has a{" "}
              <span className="text-amber-400">Sheriff</span> now.
            </h1>

            <p className="text-zinc-400 text-lg leading-relaxed mb-9 max-w-xl">
              We monitor your site 24/7 and warn you before downtime, plugin issues, or broken pages cost you leads.
            </p>

            <div className="flex flex-wrap items-center gap-4 mb-10">
              <Link to="/dashboard">
                <Button data-testid="hero-start-monitoring-btn" className="h-12 px-7 bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold rounded-md text-base transition-all hover:shadow-lg hover:shadow-amber-500/20">
                  Start Monitoring
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/dashboard">
                <Button data-testid="hero-view-dashboard-btn" variant="outline" className="h-12 px-7 border-zinc-700 bg-transparent text-zinc-200 hover:bg-zinc-800 hover:text-slate-50 rounded-md text-base">
                  View Live Dashboard
                </Button>
              </Link>
            </div>

            {/* Trust row */}
            <p data-testid="hero-supporting-line" className="text-amber-400/80 text-sm font-medium italic tracking-wide mb-4">
              Know before your customers do.
            </p>
            <div data-testid="trust-row" className="flex flex-wrap items-center gap-x-7 gap-y-3">
              <div className="flex items-center gap-2 text-zinc-500 text-sm">
                <Clock className="w-4 h-4 text-emerald-400" />
                24/7 Monitoring
              </div>
              <div className="flex items-center gap-2 text-zinc-500 text-sm">
                <Shield className="w-4 h-4 text-emerald-400" />
                100% WordPress Focused
              </div>
              <div className="flex items-center gap-2 text-zinc-500 text-sm">
                <Lock className="w-4 h-4 text-emerald-400" />
                Your data is secure
              </div>
            </div>
          </div>

          <HeroPreviewCard />
        </div>
      </section>

      {/* ===== FEATURES ===== */}
      <section id="features" className="py-24 px-6 border-t border-zinc-900">
        <div className="max-w-7xl mx-auto">
          <div className="mb-14">
            <p className="text-amber-400 text-sm font-semibold tracking-widest uppercase mb-3">Features</p>
            <h2 className="text-base md:text-lg font-semibold font-['Outfit'] text-slate-50 max-w-2xl sm:text-3xl md:text-4xl leading-tight">
              Everything a site owner needs. Nothing they don't.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {FEATURES.map((f) => (
              <div
                key={f.title}
                data-testid={`feature-card-${f.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="group bg-zinc-900/60 border border-zinc-800 rounded-xl p-7 transition-all duration-300 hover:border-amber-500/30 hover:bg-zinc-900"
              >
                <div className="w-11 h-11 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mb-5 group-hover:bg-amber-500/15 transition-colors">
                  <f.icon className="w-5 h-5 text-amber-500" />
                </div>
                <h3 className="text-slate-50 font-semibold text-lg mb-2 font-['Outfit']">{f.title}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section id="how-it-works" className="py-24 px-6 border-t border-zinc-900 relative">
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-amber-500/4 rounded-full blur-[100px] pointer-events-none" />
        <div className="max-w-7xl mx-auto relative">
          <div className="mb-14">
            <p className="text-amber-400 text-sm font-semibold tracking-widest uppercase mb-3">How It Works</p>
            <h2 className="text-base md:text-lg font-semibold font-['Outfit'] text-slate-50 max-w-2xl sm:text-3xl md:text-4xl leading-tight">
              Set it up once. The Sheriff handles the rest.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {STEPS.map((s) => (
              <div key={s.step} data-testid={`how-it-works-step-${s.step}`} className="relative bg-zinc-900/60 border border-zinc-800 rounded-xl p-7">
                <span className="absolute top-6 right-7 text-zinc-800 font-bold text-4xl font-['Outfit'] select-none">{s.step}</span>
                <div className="w-11 h-11 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-5">
                  <s.icon className="w-5 h-5 text-emerald-400" />
                </div>
                <h3 className="text-slate-50 font-semibold text-lg mb-2 font-['Outfit']">{s.title}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PROBLEM SECTION ===== */}
      <section className="py-24 px-6 border-t border-zinc-900">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-amber-500/10 border border-amber-500/25 mb-7">
            <Shield className="w-7 h-7 text-amber-500" />
          </div>
          <h2 data-testid="problem-heading" className="text-base md:text-lg font-semibold font-['Outfit'] text-slate-50 mb-6 sm:text-3xl md:text-4xl leading-tight">
            Built for owners who do not have a tech team.
          </h2>
          <p className="text-zinc-400 text-lg leading-relaxed max-w-2xl mx-auto">
            Small business owners often find out about website problems only after something breaks.
            WordPress Sheriff gives them one simple place to see if their site is{" "}
            <span className="text-emerald-400 font-medium">clean</span>,{" "}
            <span className="text-amber-400 font-medium">needs attention</span>, or{" "}
            <span className="text-red-400 font-medium">in trouble</span>.
          </p>
        </div>
      </section>

      {/* ===== BENEFITS ROW ===== */}
      <section className="py-20 px-6 border-t border-zinc-900">
        <div className="max-w-7xl mx-auto grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {BENEFITS.map((b) => (
            <div key={b.title} data-testid={`benefit-${b.title.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="flex flex-col items-start p-6 rounded-xl bg-zinc-900/40 border border-zinc-800/70">
              <b.icon className="w-6 h-6 text-amber-500 mb-4" />
              <h3 className="text-slate-50 font-semibold mb-1.5 font-['Outfit']">{b.title}</h3>
              <p className="text-zinc-500 text-sm leading-relaxed">{b.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ===== PRICING ===== */}
      <section id="pricing" className="py-24 px-6 border-t border-zinc-900 relative">
        <div className="absolute bottom-0 left-1/3 w-[500px] h-[300px] bg-amber-500/4 rounded-full blur-[100px] pointer-events-none" />
        <div className="max-w-7xl mx-auto relative">
          <div className="mb-14 text-center">
            <p className="text-amber-400 text-sm font-semibold tracking-widest uppercase mb-3">Pricing</p>
            <h2 className="text-base md:text-lg font-semibold font-['Outfit'] text-slate-50 sm:text-3xl md:text-4xl leading-tight">
              Simple plans. No surprises.
            </h2>
            <p className="text-zinc-500 text-sm mt-4">Stripe billing coming soon.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {PRICING.map((p) => (
              <div
                key={p.name}
                data-testid={`pricing-card-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`rounded-xl p-7 flex flex-col ${
                  p.highlight
                    ? "bg-zinc-900 border-2 border-amber-500/40 shadow-lg shadow-amber-500/5 relative"
                    : "bg-zinc-900/60 border border-zinc-800"
                }`}
              >
                {p.highlight && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-amber-500 text-zinc-950 text-[11px] font-bold tracking-wide">
                    MOST POPULAR
                  </span>
                )}
                <h3 className="text-slate-50 font-semibold text-lg font-['Outfit'] mb-1">{p.name}</h3>
                <p className="text-zinc-500 text-sm mb-5">{p.desc}</p>
                <p className={`font-bold font-['Outfit'] mb-6 ${p.price === "$0" ? "text-3xl text-slate-50" : "text-xl text-amber-400"}`}>
                  {p.price}
                </p>
                <ul className="space-y-2.5 mb-8 flex-1">
                  {p.features.map((feat) => (
                    <li key={feat} className="flex items-center gap-2.5 text-zinc-400 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                      {feat}
                    </li>
                  ))}
                </ul>
                <Link to="/dashboard" className="w-full">
                  <Button
                    data-testid={`pricing-cta-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={`w-full h-11 rounded-md font-semibold ${
                      p.highlight
                        ? "bg-amber-500 hover:bg-amber-400 text-zinc-950"
                        : "bg-zinc-800 hover:bg-zinc-700 text-slate-50"
                    }`}
                  >
                    {p.cta}
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section className="py-24 px-6 border-t border-zinc-900">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-base md:text-lg font-semibold font-['Outfit'] text-slate-50 mb-5 sm:text-3xl md:text-4xl leading-tight">
            Stop finding out from your customers.
          </h2>
          <p className="text-zinc-400 text-lg mb-9">
            Put a Sheriff on watch. Your site gets monitored, you get peace of mind.
          </p>
          <Link to="/dashboard">
            <Button data-testid="final-cta-btn" className="h-12 px-8 bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold rounded-md text-base transition-all hover:shadow-lg hover:shadow-amber-500/20">
              Start Monitoring
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="border-t border-zinc-900 py-10 px-6">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <Shield className="w-4 h-4 text-amber-500" />
            <span className="text-zinc-400 text-sm font-medium">WordPress Sheriff</span>
          </div>
          <p className="text-zinc-600 text-xs">
            Built with Emergent for the Build Challenge · Stripe billing coming soon
          </p>
        </div>
      </footer>
    </div>
  );
}
